import { useFlags } from 'launchdarkly-react-client-sdk';
import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { therapeuticAreaRelativesProducts } from '../basics/constants/common.constants';
import { BiogenProduct } from '../basics/enums/biogen.enums';
import useGetAllProductsLazy from '../basics/graphql/queries/getAllProductsLazy';
import useGetCRMAllProductsLazy from '../basics/graphql/queries/getCRMAllProductsLazy';
import { ProductType } from '../basics/types/products.types';
import { CurrentUserStateContext } from './user';
import { errorToast } from 'basics/utils/toast';
import { ProductsFilter } from 'generated/maya.types';

export type ProductsContextType = {
  allProducts: ProductType[];
  productList: ProductType[];
  productsLoading: boolean;
};

export const ProductsContext = createContext<ProductsContextType>({
  allProducts: [],
  productList: [],
  productsLoading: false,
});

export const ProductsProvider = ({ children }: { children: ReactNode }) => {
  const flags = useFlags();
  const [allProducts, setAllProducts] = useState<ProductType[]>([]);
  const [productList, setProductList] = useState<ProductType[]>([]);
  const userContextValue = useContext(CurrentUserStateContext);
  const { getAllProductsLazy } = useGetAllProductsLazy();
  const { getCRMAllProductsLazy } = useGetCRMAllProductsLazy();
  const [selectedCountry, setSelectedCountry] = useState(userContextValue.currentUserData?.currentUser?.country);
  const [productsLoading, setProductsLoading] = useState(false);

  const getAllProducts = useCallback(async () => {
    try {
      setProductsLoading(true);
      const { currentUser } = userContextValue.currentUserData;
      const notLoading = !userContextValue.currentUserData.loading;
      const isFilterByCountry = flags.mayaAkma519EnableProductFilteringByCountry;

      if (currentUser && notLoading && selectedCountry !== undefined) {
        if (flags.mayaBdcfcsd2599EnableDynamicProductDisplay) {
          const productFilter: ProductsFilter = { Country: selectedCountry };
          const fetchProducts = flags.mayaAkma1457EnableInternalProducts ? getAllProductsLazy : getCRMAllProductsLazy;
          const { data } = await fetchProducts({ variables: { filters: productFilter } });
          const products = data?.getAllProducts || data?.getCRMAllProducts;

          if (products) {
            const countryProducts = products.filter((product: ProductType) => product.isInCountry || !selectedCountry);
            setAllProducts(isFilterByCountry ? countryProducts : products);
            setProductList(products);
          }
        } else {
          const allStaticProducts = Object.entries(BiogenProduct).map(([key, value]) => ({
            productExternalID: value,
            productID: key,
            productName: key,
            isInCountry: false,
            CRMTherapeuticArea: Object.keys(therapeuticAreaRelativesProducts)
              .find((ta) => therapeuticAreaRelativesProducts[ta as keyof typeof therapeuticAreaRelativesProducts]
                ?.includes(value)) || '',
            therapeuticArea: Object.keys(therapeuticAreaRelativesProducts)
              .find((ta) => therapeuticAreaRelativesProducts[ta as keyof typeof therapeuticAreaRelativesProducts]
                ?.includes(value)) || '',
          }));

          setAllProducts(allStaticProducts);
          setProductList(allStaticProducts);
        }
      }
    } catch (error) {
      errorToast(error as string);
    } finally {
      setProductsLoading(false);
    }
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [
    flags.mayaBdcfcsd2599EnableDynamicProductDisplay,
    flags.mayaAkma1457EnableInternalProducts,
    userContextValue.currentUserData.loading,
    selectedCountry,
    getAllProductsLazy,
    getCRMAllProductsLazy,
  ]);

  useEffect(() => {
    getAllProducts();
  }, [getAllProducts]);

  useEffect(() => {
    setSelectedCountry(userContextValue.currentUserData?.currentUser?.country);
  }, [userContextValue.currentUserData?.currentUser?.country]);

  return (
    <ProductsContext.Provider value={ { allProducts, productList, productsLoading } }>
      { children }
    </ProductsContext.Provider>
  );
};
